import React, { useState, useCallback, useRef } from 'react';
import { playCorrect, playWrong, playClap } from '../../utils/sounds';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import LoginPromptModal from '../../components/LoginPromptModal';
import { useAuth } from '../../contexts/AuthContext';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY    = '#2DBCAF';
const PRIMARY_GR = 'linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%)';
const BG_GR      = 'linear-gradient(160deg, #f9e8d9 0%, #faf0e8 40%, #e8f5f3 100%)';
const FONT       = "'Plus Jakarta Sans', sans-serif";

// ─── Animations ───────────────────────────────────────────────────────────────
const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  40%       { transform: scale(1.15) translateY(-6px); }
  70%       { transform: scale(1.07) translateY(-3px); }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.75); }
  to   { opacity: 1; transform: scale(1); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50%       { transform: translateY(-12px) rotate(5deg); }
`;
const floatDelay = keyframes`
  0%, 100% { transform: translateY(0px) rotate(8deg); }
  50%       { transform: translateY(-8px) rotate(-3deg); }
`;

// ─── Intro ────────────────────────────────────────────────────────────────────
const IntroOverlay = styled.div`
  position: fixed; inset: 0; z-index: 900;
  background: ${BG_GR}; display: flex; flex-direction: column;
  align-items: center;
  overflow: hidden; font-family: ${FONT};
`;
const IntroWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  width: 100%;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
`;
const TopNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.1);
  padding: 10px 16px; display: flex; align-items: center;
`;
const BackBtn = styled.button`
  width: 36px; height: 36px; background: transparent; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #333; transition: color 0.2s;
  &:hover { color: ${PRIMARY}; }
  .material-symbols-outlined { font-size: 26px; }
`;
const CharArea = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  position: relative; min-height: 0;
`;
const FloatLetter = styled.div<{ $top?: string; $left?: string; $right?: string; $bottom?: string; $delay?: number; $color: string }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'}; left: ${p => p.$left ?? 'auto'};
  right: ${p => p.$right ?? 'auto'}; bottom: ${p => p.$bottom ?? 'auto'};
  width: 54px; height: 54px; border-radius: 14px;
  background: ${p => p.$color};
  display: flex; align-items: center; justify-content: center;
  font-size: 28px; font-weight: 900; color: #fff;
  box-shadow: 0 4px 14px rgba(0,0,0,0.15);
  animation: ${p => (p.$delay ?? 0) % 2 === 0 ? float : floatDelay} 3.5s ease-in-out ${p => p.$delay ?? 0}s infinite;
`;
const CharCard = styled.div`
  font-size: clamp(72px, 20vw, 110px);
  animation: ${bounce} 2s ease-in-out infinite;
  filter: drop-shadow(0 12px 28px rgba(0,0,0,0.15));
`;
const InfoCard = styled.div`
  background: #fff; border-radius: 32px 32px 0 0;
  padding: 24px 24px max(32px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 32px rgba(0,0,0,0.08);
  animation: ${fadeInUp} 0.6s ease; font-family: ${FONT};
`;
const Handle = styled.div`width: 40px; height: 4px; background: #e0e0e0; border-radius: 99px; margin: 0 auto 18px;`;
const IntroTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px); font-weight: 900; margin: 0 0 8px; color: #1a2a2a;
  span { color: ${PRIMARY}; }
`;
const IntroDesc = styled.p`font-size: 14px; color: #7a8a8a; line-height: 1.6; margin: 0 0 20px;`;
const StartBtn = styled.button`
  width: 100%; padding: 16px 24px; border-radius: 99px; border: none;
  background: ${PRIMARY_GR}; color: #fff; font-size: 17px; font-weight: 800;
  cursor: pointer; box-shadow: 0 6px 24px rgba(43,188,175,0.4); font-family: ${FONT};
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); }
  &:active { transform: translateY(0); }
`;

// ─── Game ─────────────────────────────────────────────────────────────────────
const GameWrap = styled.div`
  min-height: 100dvh; display: flex; flex-direction: column;
  background: ${BG_GR}; font-family: ${FONT};
`;
const GameNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.92); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.12);
  padding: 10px 16px; display: flex; align-items: center; justify-content: space-between;
`;
const NavLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const GameTitle = styled.span`font-size: 17px; font-weight: 800; color: #1a2a2a;`;
const ScoreBadge = styled.div`
  background: rgba(45,188,175,0.12); color: ${PRIMARY};
  padding: 6px 14px; border-radius: 10px; font-size: 14px; font-weight: 800;
`;
const GameBody = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; padding: 24px 16px; gap: 20px;
  max-width: 480px; margin: 0 auto; width: 100%;
`;
const WordCard = styled.div`
  background: #fff; border-radius: 24px; padding: 24px;
  text-align: center; box-shadow: 0 4px 24px rgba(0,0,0,0.08); width: 100%;
`;
const EmojiDisplay = styled.div`font-size: clamp(56px, 16vw, 80px); margin-bottom: 8px;`;
const WordDisplay = styled.div`
  font-size: clamp(28px, 8vw, 40px); font-weight: 900; color: #1a2a2a;
  letter-spacing: 4px; text-transform: uppercase;
`;
const FirstLetter = styled.span`color: #f59e0b;`;
const RoundInfo = styled.div`font-size: 13px; color: #7a8a8a; margin-top: 8px;`;
const OptionsGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; width: 100%;
`;
const OptionBtn = styled.button<{ $state: 'default' | 'correct' | 'incorrect' }>`
  height: 80px; border-radius: 18px; border: 3px solid;
  border-color: ${p => p.$state === 'correct' ? '#22c55e' : p.$state === 'incorrect' ? '#ef4444' : '#e2e8f0'};
  background: ${p => p.$state === 'correct' ? 'rgba(34,197,94,0.12)' : p.$state === 'incorrect' ? 'rgba(239,68,68,0.12)' : '#fff'};
  font-size: clamp(28px, 8vw, 40px); font-weight: 900; color: #1a2a2a;
  cursor: pointer; transition: all 0.2s; font-family: ${FONT};
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  &:hover:not(:disabled) { border-color: ${PRIMARY}; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(45,188,175,0.2); }
  &:disabled { cursor: default; }
`;
const FeedbackMsg = styled.div<{ $type: 'correct' | 'incorrect' }>`
  background: ${p => p.$type === 'correct' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)'};
  color: ${p => p.$type === 'correct' ? '#16a34a' : '#dc2626'};
  border-radius: 12px; padding: 10px 20px; font-size: 15px; font-weight: 700;
  animation: ${fadeInScale} 0.3s ease;
`;
const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
`;
const Modal = styled.div`
  background: #fff; border-radius: 28px; padding: 28px 24px 24px;
  text-align: center; max-width: 300px; width: 88%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  animation: ${fadeInScale} 0.4s cubic-bezier(0.175,0.885,0.32,1.275);
  font-family: ${FONT};
`;
const ModalEmoji = styled.div`font-size: 52px; margin-bottom: 10px;`;
const ModalTitle = styled.div`font-size: 24px; font-weight: 900; color: #1a2a2a; margin-bottom: 6px;`;
const ModalDesc = styled.div`font-size: 14px; color: #7a8a8a; margin-bottom: 20px; line-height: 1.5;`;
const ModalBtnRow = styled.div`display: flex; gap: 8px; justify-content: center;`;
const ModalBtn = styled.button<{ $primary?: boolean }>`
  padding: 11px 22px; border-radius: 99px;
  border: ${p => p.$primary ? 'none' : '2px solid #e0e0e0'};
  background: ${p => p.$primary ? PRIMARY_GR : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#555'};
  font-size: 14px; font-weight: 700; cursor: pointer; font-family: ${FONT};
  box-shadow: ${p => p.$primary ? '0 4px 16px rgba(43,188,175,0.4)' : 'none'};
  transition: all 0.2s;
  &:hover { transform: translateY(-1px); }
`;

// ─── Data ─────────────────────────────────────────────────────────────────────
interface LetterWord { letter: string; word: string; emoji: string; }

const LETTER_WORDS: LetterWord[] = [
  { letter: 'A', word: 'Ayam',     emoji: '🐔' },
  { letter: 'B', word: 'Bola',     emoji: '⚽' },
  { letter: 'C', word: 'Cacing',   emoji: '🪱' },
  { letter: 'D', word: 'Dadu',     emoji: '🎲' },
  { letter: 'E', word: 'Elang',    emoji: '🦅' },
  { letter: 'F', word: 'Flamingo', emoji: '🦩' },
  { letter: 'G', word: 'Gajah',    emoji: '🐘' },
  { letter: 'H', word: 'Harimau',  emoji: '🐯' },
  { letter: 'I', word: 'Ikan',     emoji: '🐟' },
  { letter: 'J', word: 'Jerapah',  emoji: '🦒' },
  { letter: 'K', word: 'Kucing',   emoji: '🐱' },
  { letter: 'L', word: 'Lebah',    emoji: '🐝' },
  { letter: 'M', word: 'Monyet',   emoji: '🐵' },
  { letter: 'N', word: 'Nanas',    emoji: '🍍' },
  { letter: 'O', word: 'Onta',     emoji: '🐫' },
  { letter: 'P', word: 'Panda',    emoji: '🐼' },
  { letter: 'R', word: 'Rusa',     emoji: '🦌' },
  { letter: 'S', word: 'Sapi',     emoji: '🐄' },
  { letter: 'T', word: 'Tikus',    emoji: '🐭' },
  { letter: 'U', word: 'Ular',     emoji: '🐍' },
  { letter: 'W', word: 'Wortel',   emoji: '🥕' },
  { letter: 'Z', word: 'Zebra',    emoji: '🦓' },
];
const MAX_ROUNDS = 10;

function speak(text: string) {
  if (!('speechSynthesis' in window)) return;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = 'id-ID'; u.rate = 0.8;
  const id = speechSynthesis.getVoices().find(v => v.lang.startsWith('id'));
  if (id) u.voice = id;
  speechSynthesis.speak(u);
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function LetterMatchGame() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [showIntro, setShowIntro] = useState(true);
  const [questions, setQuestions] = useState<LetterWord[]>([]);
  const [current, setCurrent]   = useState<LetterWord | null>(null);
  const [options, setOptions]   = useState<string[]>([]);
  const [score, setScore]       = useState(0);
  const [round, setRound]       = useState(1);
  const [btnState, setBtnState] = useState<Record<string, 'default' | 'correct' | 'incorrect'>>({});
  const [feedback, setFeedback] = useState<{ type: 'correct' | 'incorrect'; msg: string } | null>(null);
  const [gameOver, setGameOver] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const locked = useRef(false);

  const generateRound = useCallback((q: LetterWord[], r: number) => {
    const item = q[r - 1];
    const wrong = LETTER_WORDS.filter(x => x.letter !== item.letter)
      .sort(() => Math.random() - 0.5).slice(0, 3).map(x => x.letter);
    setOptions([item.letter, ...wrong].sort(() => Math.random() - 0.5));
    setCurrent(item);
    setBtnState({});
    setFeedback(null);
    setTimeout(() => speak(item.word), 400);
  }, []);

  const startGame = useCallback(() => {
    const q = [...LETTER_WORDS].sort(() => Math.random() - 0.5).slice(0, MAX_ROUNDS);
    locked.current = false;
    setQuestions(q); setScore(0); setRound(1); setGameOver(false);
    setShowLoginPrompt(false); setFeedback(null);
    generateRound(q, 1);
  }, [generateRound]);

  const handleClick = (letter: string) => {
    if (locked.current || !current) return;
    locked.current = true;
    if (letter === current.letter) {
      setBtnState(s => ({ ...s, [letter]: 'correct' }));
      setFeedback({ type: 'correct', msg: '✅ Benar! +10' });
      setScore(s => s + 10);
      playCorrect();
      setTimeout(() => {
        if (round >= MAX_ROUNDS) {
          setGameOver(true);
          locked.current = false;
          playClap();
        } else {
          const nr = round + 1;
          setRound(nr);
          generateRound(questions, nr);
          locked.current = false;
          if (!user && !authLoading && nr === 4) {
            setTimeout(() => setShowLoginPrompt(true), 500);
          }
        }
      }, 900);
    } else {
      // Wrong answer → auto-advance to next round after feedback
      setBtnState(s => ({ ...s, [letter]: 'incorrect' }));
      setFeedback({ type: 'incorrect', msg: `❌ Salah! Ini ${current.letter}` });
      playWrong();
      setTimeout(() => {
        if (round >= MAX_ROUNDS) {
          setGameOver(true);
          locked.current = false;
        } else {
          const nr = round + 1;
          setRound(nr);
          generateRound(questions, nr);
          locked.current = false;
          if (!user && !authLoading && nr === 4) {
            setTimeout(() => setShowLoginPrompt(true), 500);
          }
        }
      }, 900);
    }
  };

  if (showIntro) {
    return (
      <IntroOverlay>
        <IntroWrapper>
        <TopNav><BackBtn onClick={() => navigate('/')}><span className="material-symbols-outlined">arrow_back</span></BackBtn></TopNav>
        <CharArea>
          <FloatLetter $color="#ef4444" $top="16%" $left="8%" $delay={0}>A</FloatLetter>
          <FloatLetter $color="#3b82f6" $top="12%" $right="10%" $delay={1}>B</FloatLetter>
          <FloatLetter $color="#22c55e" $bottom="22%" $left="10%" $delay={2}>C</FloatLetter>
          <FloatLetter $color="#f97316" $bottom="18%" $right="8%" $delay={3}>D</FloatLetter>
          <CharCard>🔤</CharCard>
        </CharArea>
        <InfoCard>
          <Handle />
          <IntroTitle>Tebak <span>Huruf!</span></IntroTitle>
          <IntroDesc>Lihat gambar dan kata, lalu pilih huruf awal yang benar. Ayo belajar alfabet dengan seru!</IntroDesc>
          <StartBtn onClick={() => { setShowIntro(false); startGame(); }}>Mulai Bermain</StartBtn>
        </InfoCard>
        </IntroWrapper>
      </IntroOverlay>
    );
  }

  return (
    <GameWrap>
      <GameNav>
        <NavLeft>
          <BackBtn onClick={() => setShowIntro(true)}><span className="material-symbols-outlined">arrow_back</span></BackBtn>
          <GameTitle>Tebak Huruf</GameTitle>
        </NavLeft>
        <ScoreBadge>Skor: {score}</ScoreBadge>
      </GameNav>
      <GameBody>
        <WordCard>
          <EmojiDisplay>{current?.emoji}</EmojiDisplay>
          <WordDisplay>
            {current && (
              <>
                <FirstLetter>{current.word.charAt(0)}</FirstLetter>
                {current.word.slice(1)}
              </>
            )}
          </WordDisplay>
          <RoundInfo>Ronde {round} / {MAX_ROUNDS}</RoundInfo>
        </WordCard>

        {feedback && <FeedbackMsg $type={feedback.type}>{feedback.msg}</FeedbackMsg>}

        <OptionsGrid>
          {options.map(letter => (
            <OptionBtn
              key={letter}
              $state={btnState[letter] ?? 'default'}
              disabled={btnState[current?.letter ?? ''] === 'correct'}
              onClick={() => handleClick(letter)}
            >
              {letter}
            </OptionBtn>
          ))}
        </OptionsGrid>
      </GameBody>

      {gameOver && (
        <ModalOverlay>
          <Modal>
            <ModalEmoji>🎉</ModalEmoji>
            <ModalTitle>Selesai!</ModalTitle>
            <ModalDesc>Skor akhir: <strong>{score}</strong> dari {MAX_ROUNDS * 10}</ModalDesc>
            <ModalBtnRow>
              <ModalBtn onClick={() => navigate("/games")}>Menu</ModalBtn>
              <ModalBtn $primary onClick={startGame}>Main Lagi</ModalBtn>
            </ModalBtnRow>
          </Modal>
        </ModalOverlay>
      )}

      {showLoginPrompt && !user && !authLoading && (
        <LoginPromptModal
          
          onClose={() => setShowLoginPrompt(false)}
          subtitle="Silahkan login terlebih dahulu untuk melanjutkan permainan."
          required
        />
      )}
    </GameWrap>
  );
}
